import React, { useEffect, useState } from "react";
import { api, money, money0, shortDate, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { KpiCard, StatusPill } from "../components/UI";
import { useAuth } from "../lib/auth";
import { can } from "../lib/roles";

const NEXT_LEVEL = { null: "friendly", undefined: "friendly", friendly: "firmer", firmer: "final", final: null };
const LEVEL_LABEL = { friendly: "FRIENDLY REMINDER", firmer: "FIRMER FOLLOW-UP", final: "FINAL NOTICE" };
const LEVEL_TONE = { friendly: "blue", firmer: "amber", final: "red" };

function lastReminder(c) {
  const h = c.reminder_history || [];
  return h.length ? h[h.length - 1] : null;
}

export default function InvoicesMoneyOwed() {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [arStatus, setArStatus] = useState(null);
  const [unmatched, setUnmatched] = useState([]);
  const [tab, setTab] = useState("company");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [q, setQ] = useState("");
  const [confirm, setConfirm] = useState(null); // { customer, level }
  const [refreshing, setRefreshing] = useState(false);
  const { user } = useAuth();
  const canSend = can(user, "customers.reminders");
  const canRefresh = user && user.role !== "Read-only";

  const load = () => Promise.all([
    api.get("/company-invoices"),
    api.get("/customers"),
    api.get("/ar-sync/status"),
  ]).then(([i, c, s]) => {
    setInvoices(i.data);
    setCustomers(c.data);
    setArStatus(s.data?.status || null);
    setUnmatched(s.data?.unmatched || []);
  });
  useEffect(() => { load(); }, []);

  const totalInvoiced = invoices.reduce((s, i) => s + i.total, 0);
  const owingCustomers = customers.filter(c => c.owing_amount > 0);
  const totalOwing = owingCustomers.reduce((s, c) => s + c.owing_amount, 0);
  const bigOwing = owingCustomers.filter(c => c.owing_amount >= 50000).length;

  // Staleness: last_sync_at older than 2 hours OR last status != ok
  const lastSyncIso = arStatus?.last_sync_at;
  const lastStatus = arStatus?.last_sync_status;
  const lastError = arStatus?.last_sync_error;
  const hoursSince = lastSyncIso
    ? (Date.now() - new Date(lastSyncIso).getTime()) / (1000 * 60 * 60)
    : null;
  const isStale = !lastSyncIso || hoursSince > 2 || lastStatus !== "ok";
  const asAt = lastSyncIso
    ? new Date(lastSyncIso).toLocaleString("en-AU", {
        timeZone: "Australia/Sydney",
        hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short", year: "numeric",
      })
    : "never";

  const refreshAr = async () => {
    if (!canRefresh) return;
    setRefreshing(true); setMsg("");
    try {
      const r = await api.post("/ar-sync/refresh", {});
      if (r.data.ok) {
        setMsg(`Refreshed — $${(r.data.total_outstanding).toLocaleString("en-AU", {minimumFractionDigits:2})} across ${r.data.customer_count} customers.`);
      } else {
        setMsg(`Refresh failed: ${r.data.message || r.data.error}`);
      }
      await load();
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setRefreshing(false); }
  };

  const sendReminder = (c, level) => setConfirm({ customer: c, level });

  const doSendReminder = async () => {
    if (!confirm) return;
    const { customer: c, level } = confirm;
    setConfirm(null);
    setBusy(c.id); setMsg("");
    try {
      const r = await api.post(`/customers/${c.id}/reminders`, { level });
      setMsg(`${LEVEL_LABEL[level]} ${r.data.live ? "sent live" : "logged (Resend switched off)"} to ${c.legal_name}.`);
      await load();
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="invoices-money-owed-page">
      <PageHeader
        title="Invoices &amp; money owed"
        subtitle="Reference totals between your own companies — and who still owes us"
      />

      <div className="ach-kpi-row" style={{gridTemplateColumns:"repeat(4, minmax(0,1fr))"}}>
        <KpiCard tone="maroon" number={money0(totalInvoiced)} label="Entity reference totals (crane only)" testid="im-kpi-invoiced" />
        <KpiCard tone="amber" number={money0(totalOwing)} label="Money owed to us" testid="im-kpi-owing" />
        <KpiCard tone="red" number={bigOwing} label="Customers owing $50k+" testid="im-kpi-big" />
        <KpiCard tone="neutral" number={invoices.length} label="Reference rows generated" testid="im-kpi-count" />
      </div>

      <div className="ach-card" style={{marginBottom: 16}}>
        <div style={{display:"flex", gap: 8, marginBottom: 14, alignItems:"center", flexWrap:"wrap"}}>
          <button className={`ach-btn ${tab==="company" ? "ach-btn--primary" : "ach-btn--grey"}`} onClick={()=>setTab("company")} data-testid="tab-company">
            Entity reference totals
          </button>
          <button className={`ach-btn ${tab==="customers" ? "ach-btn--primary" : "ach-btn--grey"}`} onClick={()=>setTab("customers")} data-testid="tab-customers">
            Who owes us
          </button>
          <div style={{flex:1}} />
          <input placeholder={tab === "company" ? "Search company, month…" : "Search customer, ABN…"} value={q} onChange={(e)=>setQ(e.target.value)}
            data-testid="invoices-search"
            style={{border:"1px solid var(--ach-card-border)", borderRadius:6, padding:"8px 12px", fontSize:13, width: 280, maxWidth:"100%", boxSizing:"border-box"}} />
        </div>

        {msg && (
          <div style={{background:"var(--ach-green-tint)", color:"var(--ach-green)", padding:10, borderRadius:8, marginBottom:12, fontSize:12}} data-testid="reminder-msg">{msg}</div>
        )}

        {tab === "company" && (
          <>
          <div style={{fontSize:12, color:"var(--ach-text-2)", marginBottom:12, fontStyle:"italic"}}>
            Reference totals only — not sent to Xero. Crane hire (code 200) is what each owning company would invoice ACH; transport and ancillary are billed directly to the customer by ACH and are shown here as excluded. Raise the real inter-entity invoices in Xero yourself.
          </div>
          <div className="ach-table-wrap">
            <table className="ach-table">
              <thead>
                <tr>
                  <th>From (owning company)</th>
                  <th>To</th>
                  <th>Month</th>
                  <th>Kind</th>
                  <th>Lines</th>
                  <th className="money">Crane (code 200)</th>
                  <th className="money">Excluded</th>
                  <th>Generated</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td colSpan={8} style={{textAlign:"center", padding: 24, color: "var(--ach-text-2)"}}>
                    No entity reference totals yet — close month-end to generate them.
                  </td></tr>
                ) : invoices.filter(i => {
                  if (!q) return true;
                  const s = q.toLowerCase();
                  return ((i.from_company||"").toLowerCase().includes(s) ||
                          (i.to_company||"").toLowerCase().includes(s) ||
                          (i.month||"").toLowerCase().includes(s));
                }).map(i => {
                  const hasBreakdown = i.excluded_total !== undefined && i.excluded_total !== null;
                  const excluded = hasBreakdown ? i.excluded_total : 0;
                  const bd = i.excluded_breakdown || {};
                  const bits = Object.entries(bd)
                    .filter(([_, v]) => v > 0)
                    .map(([ct, v]) => `${ct} ${money(v)}`)
                    .join(" · ");
                  return (
                    <tr key={i.id} data-testid={`entity-row-${i.id}`}>
                      <td><div className="unit-num">{i.from_company}</div></td>
                      <td>{i.to_company}</td>
                      <td>{i.month}</td>
                      <td><StatusPill tone={i.kind === "external-summary" ? "blue" : "neutral"}>{i.kind === "external-summary" ? "EMAIL SUMMARY" : "INTERNAL"}</StatusPill></td>
                      <td>{i.lines.length}</td>
                      <td className="money"><strong>{money(i.total)}</strong></td>
                      <td className="money" style={{color:"var(--ach-text-2)", fontSize:12}}>
                        {hasBreakdown && excluded > 0
                          ? <span title={bits}>{money(excluded)}<br/><span style={{opacity:0.7, fontSize:11}}>{bits}</span></span>
                          : (hasBreakdown ? <span style={{opacity:0.5}}>—</span>
                                          : <span style={{opacity:0.5}} title="Closed before 7 Sep 2026 — crane and ancillary combined">combined</span>)}
                      </td>
                      <td>{shortDate(i.generated_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}

        {tab === "customers" && (
          <>
          {/* Freshness header */}
          <div style={{
            display:"flex", alignItems:"center", gap:12, flexWrap:"wrap",
            padding:"10px 12px", marginBottom:12, borderRadius:8,
            background: isStale ? "var(--ach-amber-tint, #fff6e0)" : "var(--ach-green-tint, #eafbea)",
            border: `1px solid ${isStale ? "var(--ach-amber, #c88c13)" : "var(--ach-green, #1b7a3e)"}`,
            fontSize:12,
          }} data-testid="ar-freshness-banner">
            <strong style={{color: isStale ? "var(--ach-amber, #c88c13)" : "var(--ach-green, #1b7a3e)"}}>
              {isStale ? "⚠ Figures may be stale" : "✓ Up to date with Xero"}
            </strong>
            <span>As at {asAt} AEST</span>
            {lastStatus === "failed" && <span style={{color:"var(--ach-red)"}}>· last sync failed: {lastError}</span>}
            {arStatus?.last_invoice_count !== undefined &&
              <span style={{color:"var(--ach-text-2)"}}>· {arStatus.last_invoice_count} unpaid invoices · {arStatus.last_customer_count} customers</span>}
            <div style={{flex:1}} />
            {canRefresh && (
              <button className="ach-btn ach-btn--ghost ach-btn--row" onClick={refreshAr} disabled={refreshing}
                      data-testid="ar-refresh-btn">
                {refreshing ? "Refreshing from Xero…" : "Refresh now →"}
              </button>
            )}
          </div>

          <div className="ach-table-wrap">
            <table className="ach-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Account</th>
                  <th className="money">Owed to us</th>
                  <th>Aging</th>
                  <th>Next reminder</th>
                  <th>Last reminded</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {owingCustomers.filter(c => {
                  if (!q) return true;
                  const s = q.toLowerCase();
                  return ((c.legal_name||"").toLowerCase().includes(s) || (c.abn||"").includes(s));
                }).sort((a,b)=>b.owing_amount - a.owing_amount).map(c => {
                  const last = lastReminder(c);
                  const nextLevel = NEXT_LEVEL[last?.level ?? null];
                  const finished = last?.level === "final";
                  const meta = c.owing_meta || {};
                  const buckets = meta.buckets || {};
                  const bucketBits = ["90+", "61-90", "31-60", "1-30"]
                    .filter(k => (buckets[k] || 0) > 0)
                    .map(k => `${k}d ${money(buckets[k])}`);
                  const bucketTone = (buckets["90+"] || 0) > 0 ? "red"
                                   : (buckets["61-90"] || 0) > 0 ? "amber"
                                   : (buckets["31-60"] || 0) > 0 ? "amber"
                                   : "neutral";
                  return (
                    <tr key={c.id} data-testid={`owing-row-${c.id}`}>
                      <td><div className="unit-num">{c.legal_name}</div><div className="subtitle">ABN {c.abn}</div></td>
                      <td><StatusPill tone={c.account_status === "approved" ? "green" : c.account_status === "needs-recheck" ? "red" : "amber"}>
                        {c.account_status.replace(/-/g, " ").toUpperCase()}
                      </StatusPill></td>
                      <td className="money"><strong>{money(c.owing_amount)}</strong></td>
                      <td style={{fontSize:11}}>
                        {bucketBits.length > 0 ? (
                          <div data-testid={`aging-${c.id}`}>
                            <StatusPill tone={bucketTone}>
                              {bucketBits[0]}
                            </StatusPill>
                            {bucketBits.length > 1 && <div className="subtitle" style={{marginTop:4}}>{bucketBits.slice(1).join(" · ")}</div>}
                            {(buckets.current || 0) > 0 && <div className="subtitle" style={{marginTop:2, color:"var(--ach-text-2)"}}>current {money(buckets.current)}</div>}
                          </div>
                        ) : (
                          <span style={{color:"var(--ach-text-2)"}}>current</span>
                        )}
                      </td>
                      <td>
                        {finished ? (
                          <StatusPill tone="red" testid={`phone-call-${c.id}`}>TIME FOR A PHONE CALL</StatusPill>
                        ) : nextLevel ? (
                          <StatusPill tone={LEVEL_TONE[nextLevel]} testid={`next-level-${c.id}`}>{LEVEL_LABEL[nextLevel]}</StatusPill>
                        ) : (
                          <span style={{color:"var(--ach-text-2)", fontSize: 12}}>—</span>
                        )}
                      </td>
                      <td style={{fontSize: 12}}>
                        {last ? (
                          <div>
                            <div>{LEVEL_LABEL[last.level]}</div>
                            <div className="subtitle">{shortDate(last.sent_at)} · by {last.sent_by_name}</div>
                          </div>
                        ) : <span style={{color:"var(--ach-text-2)"}}>Never</span>}
                      </td>
                      <td>
                        {finished ? (
                          user?.role === "Director" ? (
                            <span style={{fontSize:12, color:"var(--ach-red)", fontWeight:600}}>Peter/Todd to call</span>
                          ) : (
                            <span style={{fontSize:12, color:"var(--ach-text-2)"}}>Flagged to a Director</span>
                          )
                        ) : nextLevel && canSend ? (
                          <button className="ach-btn ach-btn--ghost ach-btn--row" onClick={()=>sendReminder(c, nextLevel)} disabled={busy === c.id}
                            data-testid={`send-reminder-${c.id}`}>
                            {busy === c.id ? "Sending…" : `Send ${LEVEL_LABEL[nextLevel].toLowerCase()} →`}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
                {owingCustomers.length === 0 && (
                  <tr><td colSpan={7} style={{textAlign:"center", padding: 24, color: "var(--ach-text-2)"}}>
                    {isStale
                      ? "No matched outstanding — but the figures above may be stale. Click Refresh now."
                      : "Nothing owing from any customer. Nice."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Unmatched contacts */}
          {unmatched.length > 0 && (
            <div style={{marginTop:20, padding:12, borderRadius:8, border:"1px solid var(--ach-card-border)", background:"var(--ach-amber-tint, #fff6e0)"}}>
              <h3 style={{margin:"0 0 6px 0", fontSize:14}}>Xero contacts with a balance but no ACH customer link</h3>
              <div style={{fontSize:12, color:"var(--ach-text-2)", marginBottom:10}}>
                These are owed money in Xero but we don't have them linked to a customer here — reminders and the main total above miss them. Link each one in Customer setup to include them.
              </div>
              <div className="ach-table-wrap">
                <table className="ach-table">
                  <thead>
                    <tr>
                      <th>Xero contact</th>
                      <th className="money">Owed</th>
                      <th>Invoices</th>
                      <th>Oldest</th>
                      <th>Xero ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatched.map(u => (
                      <tr key={u.contact_id} data-testid={`unmatched-${u.contact_id}`}>
                        <td>{u.contact_name}</td>
                        <td className="money"><strong>{money(u.amount_due)}</strong></td>
                        <td>{u.invoice_count}</td>
                        <td style={{fontSize:12}}>{u.oldest_unpaid_date || "—"}</td>
                        <td style={{fontSize:11, color:"var(--ach-text-2)", fontFamily:"monospace"}}>{u.contact_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {confirm && (
        <div className="ach-modal-backdrop" data-testid="confirm-reminder-dialog" onClick={()=>setConfirm(null)}>
          <div className="ach-modal" style={{maxWidth: 460}} onClick={(e)=>e.stopPropagation()}>
            <div className="ach-modal__header">
              <h2 style={{margin:0,fontSize:16,fontWeight:600}}>Send {LEVEL_LABEL[confirm.level].toLowerCase()} to {confirm.customer.legal_name}?</h2>
            </div>
            <div className="ach-modal__body">
              <p style={{margin:0, fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5}}>
                They currently owe <strong>{money(confirm.customer.owing_amount)}</strong>. If Resend is switched on the email goes out now; if not it&apos;s logged to Emails as a would-send.
              </p>
            </div>
            <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
              <button className="ach-btn ach-btn--grey" onClick={()=>setConfirm(null)} data-testid="confirm-reminder-cancel">Cancel</button>
              <button className="ach-btn ach-btn--primary" onClick={doSendReminder} data-testid="confirm-reminder-go">Yes, send it →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
